import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Trash2, Camera, Pencil } from 'lucide-react';
import {
  cashbookApi, accountingApi,
  type CashbookData, type CashbookEntryInput, type CashbookImportRow, type CashKind, type Account,
} from '../../lib/api';
import { fileToBase64 } from '../receipts/scan';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Button, Card, EmptyState, Field, PageHeader, euro } from '../ui';

const today = () => new Date().toISOString().slice(0, 10);
function toCents(s: string): number {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

const KIND_LABEL: Record<CashKind, string> = {
  income: 'Einnahme',
  expense: 'Ausgabe',
  privateinlage: 'Privateinlage',
  privatentnahme: 'Privatentnahme',
};

interface Draft {
  /** Gesetzt = bestehende Buchung wird geaendert (Storno + Neubuchung). */
  editId?: string;
  date: string;
  kind: CashKind;
  gross: string;
  taxRate: string;
  accountNumber: string;
  description: string;
}
const emptyDraft = (): Draft => ({ date: today(), kind: 'expense', gross: '', taxRate: '19', accountNumber: '', description: '' });

/**
 * Auf das Ergebnis eines Import-Auftrags warten.
 *
 * Die Auswertung laeuft auf dem Server, nicht im Browser — sie ueberlebt also
 * einen Reload. Hier wird nur regelmaessig der Stand abgefragt und der
 * Fortschritt angezeigt ("Abschnitt 3 von 12").
 */
async function warteAufImport(
  companyId: string,
  jobId: string,
  status: (s: string) => void,
): Promise<Awaited<ReturnType<typeof cashbookApi.importStatus>>> {
  // Grosszuegige Obergrenze: 10 Minuten decken auch ein umfangreiches
  // Jahres-Kassenbuch ab, ohne bei einem echten Haenger ewig zu warten.
  for (let i = 0; i < 300; i++) {
    const r = await cashbookApi.importStatus(companyId, jobId);
    if (r.status === 'done' || r.status === 'failed') return r;
    status(
      r.total > 1
        ? `Tabelle wird ausgewertet – Abschnitt ${Math.min(r.done + 1, r.total)} von ${r.total} …`
        : 'Beleg wird ausgewertet …',
    );
    await new Promise((res) => setTimeout(res, 2000));
  }
  throw new Error('Die Auswertung dauert ungewöhnlich lange. Bitte die Seite später erneut öffnen.');
}

export default function Cashbook(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const toast = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<CashbookData | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [openingEdit, setOpeningEdit] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  /** Was das System gerade tut — bei mehreren Seiten dauert es sonst still. */
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [importRows, setImportRows] = useState<CashbookImportRow[] | null>(null);

  const expenseAccounts = accounts.filter((a) => a.type === 'expense');
  const revenueAccounts = accounts.filter((a) => a.type === 'revenue');

  async function load() {
    if (!active) return;
    setLoading(true);
    try {
      setData(await cashbookApi.list(active.id, year));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!active) return;
    accountingApi.accounts(active.id).then(setAccounts).catch(() => setAccounts([]));
  }, [active]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, year]);

  async function saveOpening() {
    if (!active || openingEdit === null) return;
    setBusy(true);
    try {
      await cashbookApi.setOpening(active.id, year, toCents(openingEdit));
      toast.success('Anfangsbestand gespeichert.');
      setOpeningEdit(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  async function saveEntry() {
    if (!active || !draft) return;
    const needsAccount = draft.kind === 'income' || draft.kind === 'expense';
    const gross = toCents(draft.gross);
    if (gross <= 0) { toast.error('Bitte einen Betrag größer als 0 angeben.'); return; }
    if (needsAccount && !draft.accountNumber) { toast.error('Bitte ein Buchungskonto wählen.'); return; }
    setBusy(true);
    try {
      const input: CashbookEntryInput = {
        date: draft.date,
        kind: draft.kind,
        grossCents: gross,
        taxRate: needsAccount ? Number(draft.taxRate) || 0 : undefined,
        accountNumber: needsAccount ? draft.accountNumber : null,
        description: draft.description || undefined,
      };
      if (draft.editId) {
        await cashbookApi.update(active.id, draft.editId, input);
        toast.success('Buchung geändert – die alte Fassung wurde storniert.');
      } else {
        await cashbookApi.create(active.id, input);
        toast.success('Kassenbuchung gespeichert.');
      }
      setDraft(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  /**
   * Bestehende Buchung ins Formular laden.
   *
   * Der Betrag ist brutto (wie erfasst), Konto und Steuersatz kommen aus der
   * Buchung — sonst muesste der Nutzer alles neu eintippen und koennte dabei
   * versehentlich etwas anderes buchen als vorher.
   */
  function bearbeiten(e: CashbookData['entries'][number]) {
    setDraft({
      editId: e.id,
      date: e.date.slice(0, 10),
      kind: e.kind,
      gross: (e.grossCents / 100).toFixed(2).replace('.', ','),
      taxRate: String(e.taxRate ?? 0),
      accountNumber: e.account?.number ?? '',
      description: e.description ?? '',
    });
  }

  async function reverse(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Buchung stornieren?', message: 'Die Buchung wird GoBD-konform per Gegenbuchung storniert (nicht gelöscht).', confirmLabel: 'Stornieren', danger: true });
    if (!ok) return;
    try {
      await cashbookApi.reverse(active.id, id);
      toast.success('Buchung storniert.');
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  /**
   * MIME-Typ der Datei. Der Browser laesst ihn bei Excel/CSV oft leer, deshalb
   * notfalls aus der Endung ableiten — sonst landet eine Tabelle als "Bild"
   * beim Server und wird nicht als Tabelle gelesen.
   */
  function mimeOf(f: File): string {
    if (f.type) return f.type;
    const ext = f.name.toLowerCase().split('.').pop() ?? '';
    if (ext === 'xlsx' || ext === 'xlsm') {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (ext === 'csv') return 'text/csv';
    if (ext === 'pdf') return 'application/pdf';
    return 'image/jpeg';
  }

  async function onImportFiles(e: React.ChangeEvent<HTMLInputElement>) {
    if (!active) return;
    const list = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (list.length === 0) return;
    setImporting(true);
    setImportStatus(list.length > 1 ? `${list.length} Dateien werden gelesen …` : 'Datei wird gelesen …');
    try {
      const files = await Promise.all(
        list.map(async (f) => ({ data: await fileToBase64(f), mime: mimeOf(f) })),
      );
      // Ein Jahres-Kassenbuch braucht spuerbar Zeit. Ohne Hinweis wirkt die
      // Oberflaeche eingefroren — deshalb sagen, was gerade passiert.
      const tabelle = files.some((f) => /sheet|excel|xlsx|csv/i.test(f.mime));
      setImportStatus(
        tabelle
          ? 'Tabelle wird ausgewertet – bei einem ganzen Jahr dauert das bis zu einer Minute …'
          : 'Beleg wird ausgewertet …',
      );
      // Die Auswertung laeuft im Hintergrund weiter, auch wenn die Seite
      // geschlossen wird — hier warten wir nur auf das Ergebnis.
      const { jobId } = await cashbookApi.importPhotos(active.id, files);
      const r = await warteAufImport(active.id, jobId, setImportStatus);

      if (r.entries.length === 0) {
        toast.error(r.error ?? r.warnings[0] ?? 'Keine Buchungen erkannt.');
        return;
      }
      // Alle Warnungen zeigen, nicht nur die erste: fehlt ein Monat, muss der
      // Nutzer wissen WELCHER — sonst bucht er ein unvollstaendiges Jahr.
      for (const w of r.warnings) toast.error(w);
      // Bereits gebuchte Zeilen abgewaehlt vorbelegen — sie stehen schon im
      // Kassenbuch und wuerden sonst doppelt erfasst.
      setImportRows(r.entries.map((e) => ({ ...e, selected: !e.alreadyBooked })));
    } catch (e2) { toast.error(errMsg(e2)); } finally {
      setImporting(false);
      setImportStatus(null);
    }
  }

  async function confirmImport() {
    if (!active || !importRows) return;
    const entries: CashbookEntryInput[] = importRows
      .filter((r) => r.selected !== false && (r.grossCents ?? 0) > 0)
      .map((r) => ({
        date: r.date || today(),
        kind: (r.direction === 'income' ? 'income' : 'expense') as CashKind,
        grossCents: r.grossCents ?? 0,
        taxRate: r.taxRate ?? 0,
        accountNumber: r.accountNumber,
        description: r.description ?? undefined,
      }));
    if (entries.length === 0) { toast.error('Keine gültigen Zeilen.'); return; }
    setBusy(true);
    try {
      const res = await cashbookApi.confirmImport(active.id, entries);
      toast.success(`${res.booked} Buchung(en) übernommen${res.errors.length ? `, ${res.errors.length} übersprungen` : ''}.`);
      setImportRows(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const negative = data ? data.closingCents < 0 : false;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Kassenbuch" subtitle="Bareinnahmen und -ausgaben – jede Buchung läuft über das Kassenkonto." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex gap-2 sm:ml-auto">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf,.pdf,.xlsx,.xlsm,.csv"
            multiple
            className="hidden"
            onChange={onImportFiles}
          />
          <Button variant="secondary" busy={importing} onClick={() => fileRef.current?.click()}>
            <Camera className="mr-1.5 inline h-4 w-4" />Import
          </Button>
          <Button onClick={() => setDraft(emptyDraft())}>+ Buchung</Button>
        </div>
      </div>

      {/* Statuszeile: sagt waehrend der Auswertung, was gerade passiert.
          Ohne sie wirkt die Seite bei einem Jahres-Kassenbuch eingefroren. */}
      {importStatus && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 dark:border-blue-900/40 dark:bg-blue-950/20">
          <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <span className="text-sm text-blue-900 dark:text-blue-200">{importStatus}</span>
        </div>
      )}

      {/* Kopf: Anfangsbestand + Endsaldo */}
      {data && (
        <Card className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Anfangsbestand {year}:</span>
            {openingEdit === null ? (
              <>
                <strong className="text-gray-900 dark:text-gray-100">{euro(data.openingCents)}</strong>
                <button onClick={() => setOpeningEdit((data.openingCents / 100).toFixed(2).replace('.', ','))}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400">ändern</button>
              </>
            ) : (
              <span className="inline-flex items-center gap-2">
                <input value={openingEdit} onChange={(e) => setOpeningEdit(e.target.value)} placeholder="0,00"
                  className="w-28 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800" />
                <Button onClick={saveOpening} busy={busy}>Speichern</Button>
                <button onClick={() => setOpeningEdit(null)} className="text-xs text-gray-500 hover:underline">Abbrechen</button>
              </span>
            )}
          </div>
          <div className="text-sm">
            <span className="text-gray-600 dark:text-gray-400">Kassenbestand: </span>
            <strong className={negative ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>{euro(data.closingCents)}</strong>
          </div>
        </Card>
      )}
      {negative && (
        <div className="mb-4 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
          ⚠️ Der Kassenbestand ist negativ. Eine Barkasse kann nicht weniger als 0 € enthalten – bitte Anfangsbestand oder Buchungen prüfen.
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : !data || data.entries.length === 0 ? (
        <EmptyState title="Noch keine Kassenbuchungen" hint="Erfasse deine erste Bareinnahme/-ausgabe oder importiere Foto, PDF bzw. Excel-Tabelle." action={<Button onClick={() => setDraft(emptyDraft())}>+ Buchung</Button>} />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Beleg-Nr.</th>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Beschreibung</th>
                <th className="px-3 py-2">Konto</th>
                <th className="px-3 py-2 text-right">Einnahme</th>
                <th className="px-3 py-2 text-right">Ausgabe</th>
                <th className="px-3 py-2 text-right">Saldo</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className={`border-b border-gray-100 dark:border-gray-800 ${e.isReversal || e.reversed ? 'text-gray-400 line-through' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs">{e.documentRef ?? '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(e.date).toLocaleDateString('de-DE')}</td>
                  <td className="px-3 py-2">{e.description}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{e.account ? `${e.account.number} ${e.account.name}` : '—'}</td>
                  <td className="px-3 py-2 text-right text-emerald-600 dark:text-emerald-400">{e.direction === 'income' ? euro(e.grossCents) : ''}</td>
                  <td className="px-3 py-2 text-right text-rose-600 dark:text-rose-400">{e.direction === 'expense' ? euro(e.grossCents) : ''}</td>
                  <td className="px-3 py-2 text-right font-medium">{euro(e.balanceCents)}</td>
                  <td className="px-3 py-2 text-right">
                    {!e.isReversal && !e.reversed && (
                      <span className="inline-flex items-center gap-2">
                        <button
                          onClick={() => bearbeiten(e)}
                          title="Bearbeiten (storniert die alte Buchung und bucht neu)"
                          className="text-gray-400 hover:text-blue-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => reverse(e.id)} title="Stornieren" className="text-gray-400 hover:text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal: neue Buchung */}
      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDraft(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{draft.editId ? 'Kassenbuchung bearbeiten' : 'Neue Kassenbuchung'}</h3>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Art</span>
                <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as CashKind })}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                  {(Object.keys(KIND_LABEL) as CashKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Datum" type="date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} />
                <Field label="Betrag (€)" value={draft.gross} onChange={(v) => setDraft({ ...draft, gross: v })} placeholder="0,00" />
              </div>
              {(draft.kind === 'income' || draft.kind === 'expense') && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Buchungskonto (SKR)</span>
                    <select value={draft.accountNumber} onChange={(e) => setDraft({ ...draft, accountNumber: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                      <option value="">— Konto wählen —</option>
                      {(draft.kind === 'income' ? revenueAccounts : expenseAccounts).map((a) => (
                        <option key={a.id} value={a.number}>{a.number} · {a.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">USt-Satz</span>
                    <select value={draft.taxRate} onChange={(e) => setDraft({ ...draft, taxRate: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                      <option value="19">19 %</option>
                      <option value="7">7 %</option>
                      <option value="0">0 % / steuerfrei</option>
                    </select>
                  </label>
                </>
              )}
              <Field label="Beschreibung" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="z. B. Wareneinkauf" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>Abbrechen</Button>
              <Button onClick={saveEntry} busy={busy}>{draft.editId ? 'Änderung buchen' : 'Buchen'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Import-Vorschau (Foto, PDF, Tabelle) */}
      {importRows && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setImportRows(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Erkannte Buchungen prüfen</h3>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Korrigiere bei Bedarf, dann übernehmen. Konto fehlt? In der Zeile wählen.
              Bereits gebuchte Zeilen sind abgewählt – Haken setzen, um sie trotzdem zu übernehmen.
            </p>
            <div className="space-y-2">
              {importRows.map((r, i) => (
                <div
                  key={i}
                  // Bereits gebuchte Zeilen zurueckhaltend darstellen, damit
                  // auf einen Blick klar ist, was uebernommen wird.
                  className={`grid grid-cols-12 items-center gap-2 rounded-lg border p-2 text-sm ${
                    r.selected === false
                      ? 'border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-800/40'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={r.selected !== false}
                    title={r.alreadyBooked ? 'Steht bereits im Kassenbuch' : 'Übernehmen'}
                    onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, selected: e.target.checked } : x))}
                    className="col-span-1 h-4 w-4 justify-self-center rounded border-gray-300 text-blue-600"
                  />
                  <input type="date" value={r.date ?? today()} onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, date: e.target.value } : x))}
                    className="col-span-2 rounded border border-gray-300 px-1.5 py-1 dark:border-gray-700 dark:bg-gray-800" />
                  <input value={r.description ?? ''} onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                    placeholder="Beschreibung" className="col-span-3 rounded border border-gray-300 px-1.5 py-1 dark:border-gray-700 dark:bg-gray-800" />
                  <select value={r.direction} onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, direction: e.target.value as 'income' | 'expense' } : x))}
                    className="col-span-2 rounded border border-gray-300 px-1.5 py-1 dark:border-gray-700 dark:bg-gray-800">
                    <option value="expense">Ausgabe</option>
                    <option value="income">Einnahme</option>
                  </select>
                  <select value={r.accountNumber ?? ''} onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, accountNumber: e.target.value || null } : x))}
                    className="col-span-2 rounded border border-gray-300 px-1.5 py-1 dark:border-gray-700 dark:bg-gray-800">
                    <option value="">Konto…</option>
                    {(r.direction === 'income' ? revenueAccounts : expenseAccounts).map((a) => <option key={a.id} value={a.number}>{a.number}</option>)}
                  </select>
                  <input value={r.grossCents != null ? (r.grossCents / 100).toFixed(2).replace('.', ',') : ''}
                    onChange={(e) => setImportRows(importRows.map((x, j) => j === i ? { ...x, grossCents: toCents(e.target.value) } : x))}
                    placeholder="0,00" className="col-span-2 rounded border border-gray-300 px-1.5 py-1 text-right dark:border-gray-700 dark:bg-gray-800" />
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              {importRows.some((r) => r.alreadyBooked) && (
                <span className="mr-auto text-xs text-gray-500 dark:text-gray-400">
                  {importRows.filter((r) => r.alreadyBooked).length} Zeile(n) stehen bereits im
                  Kassenbuch und sind abgewählt.
                </span>
              )}
              <Button variant="secondary" onClick={() => setImportRows(null)}>Abbrechen</Button>
              <Button onClick={confirmImport} busy={busy}>
                {importRows.filter((r) => r.selected !== false).length} übernehmen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
